import React, { FC, Fragment, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Layers, Copy, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUIStore } from '@/stores';
import { CalculatedEstimateItem, EstimateItem, ServiceCategory as Category } from '@/shared/model';
import type { TypedAdjustment } from '@/entities/estimate/model/types';
import { applyTypedAdjustment } from '@/entities/estimate';
import { NumberInput } from '@/shared/ui/NumberInput';
import { roundTo } from '@/shared/lib/number';

interface EstimateTableProps {
    items: CalculatedEstimateItem[];
    categories?: Category[];
    onUpdateItem: (id: string, field: keyof EstimateItem, value: EstimateItem[keyof EstimateItem]) => void;
    onRemoveItem: (id: string) => void;
    onCopyToRooms?: (item: CalculatedEstimateItem) => void;
    onOpenPackages: () => void;
    onOpenServices: () => void;
    onShowManualInfo: () => void;
    onShowAutoInfo: (item: CalculatedEstimateItem) => void;
    onResetPriceToCatalog?: (id: string) => void;
}

/** Typed adjustment ops for the selector. */
const ADJ_OPS: { op: TypedAdjustment['op']; symbol: string }[] = [
    { op: 'mul', symbol: '×' },
    { op: 'add', symbol: '+' },
    { op: 'sub', symbol: '−' },
];

interface AdjustmentControlProps {
    item: CalculatedEstimateItem;
    onUpdateItem: EstimateTableProps['onUpdateItem'];
}

/**
 * Inline typed-adjustment control: op selector (×/+/−) + numeric value input.
 * Hides when adjustment is identity (op='mul', value=1).
 * Emits onUpdateItem(id, 'adjustment', { op, value }) on every change.
 */
const AdjustmentControl: FC<AdjustmentControlProps> = ({ item, onUpdateItem }) => {
    const { t } = useTranslation();
    const adj = item.adjustment;
    const currentOp = adj?.op ?? 'mul';
    const currentValue = adj?.value ?? 1;

    const handleOpChange = (op: TypedAdjustment['op']) => {
        onUpdateItem(item.id, 'adjustment', { op, value: currentValue } as EstimateItem[keyof EstimateItem]);
    };

    const handleValueChange = (value: number) => {
        onUpdateItem(item.id, 'adjustment', { op: currentOp, value } as EstimateItem[keyof EstimateItem]);
    };

    return (
        <div className="flex items-center gap-1 mt-1">
            <select
                value={currentOp}
                onChange={(e) => handleOpChange(e.target.value as TypedAdjustment['op'])}
                aria-label={t('editor.table.adj_op', 'Adjustment operator')}
                data-testid="adj-op"
                className="h-6 px-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs font-mono font-bold text-slate-600 dark:text-slate-300 outline-none cursor-pointer"
            >
                {ADJ_OPS.map(({ op, symbol }) => (
                    <option key={op} value={op}>
                        {symbol}
                    </option>
                ))}
            </select>
            <input
                type="number"
                inputMode="decimal"
                step="any"
                value={currentValue}
                onChange={(e) => {
                    const parsed = parseFloat(e.target.value);
                    if (!isNaN(parsed)) handleValueChange(parsed);
                }}
                aria-label={t('editor.table.adj_value', 'Adjustment value')}
                className="w-14 h-6 px-1 text-xs font-mono text-right bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded outline-none focus:border-blue-400"
            />
        </div>
    );
};

const EstimateTableBase: FC<EstimateTableProps> = ({
    items,
    categories = [],
    onUpdateItem,
    onRemoveItem,
    onCopyToRooms,
    onOpenPackages,
    onOpenServices,
    onShowManualInfo,
    onShowAutoInfo,
    onResetPriceToCatalog,
}) => {
    const { t } = useTranslation();
    const currency = useUIStore((s) => s.currency);

    // Grouping items by category (memoized for perf)
    const groupedItems = useMemo(() => items.reduce<Record<string, CalculatedEstimateItem[]>>((acc, item) => {
        const catId = item.categoryId || item.category || 'other';
        if (!acc[catId]) {
            acc[catId] = [];
        }
        acc[catId].push(item);
        return acc;
    }, {}), [items]);

    // Get category name
    const getCategoryName = (catId: string) => {
        const cat = categories.find(c => c.id === catId);
        if (cat) return cat.nameKey ? t(cat.nameKey, cat.name) : cat.name;
        return t(`category.${catId}.name`, catId.charAt(0).toUpperCase() + catId.slice(1));
    };

    // Calculate category subtotal
    const getCategorySubtotal = (catId: string) => {
        const catItems = groupedItems[catId] || [];
        return catItems.reduce((sum, item) => sum + (item.cost || 0), 0);
    };

    // Calculate total room cost
    const totalCost = items.reduce((sum, item) => sum + (item.cost || 0), 0);
    const itemsTotal = totalCost;

    // Pure derived zebra: flat order for striping across categories (memoized)
    const flatItemIds = useMemo(() => Object.values(groupedItems).flat().map((i) => i.id), [groupedItems]);

    return (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-0 relative transition-colors">
            <div className="p-4 border-b border-blue-100 dark:border-slate-800 bg-blue-50/60 dark:bg-slate-800/60 backdrop-blur-sm flex justify-between items-center sticky top-0 z-30 rounded-t-2xl">
                <h3 className="font-bold text-slate-700 dark:text-slate-200">{t('editor.estimate_title', 'Смета работ')}</h3>
                <div className="flex gap-2">
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={onOpenPackages}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg font-bold text-xs flex items-center gap-2 shadow-md shadow-purple-200 dark:shadow-none hover:bg-purple-700 transition-all"
                    >
                        <Layers size={16}/> {t('editor.packages_btn', 'Пакеты')}
                    </motion.button>

                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={onOpenServices}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold text-xs flex items-center gap-2 shadow-md shadow-blue-200 dark:shadow-none hover:bg-blue-700 transition-all"
                    >
                        <Plus size={16}/> {t('editor.service_btn', 'Услуга')}
                    </motion.button>
                </div>
            </div>
            <div>
                {items.length === 0 ? (
                    <div className="p-10 text-center text-slate-400 dark:text-slate-600 text-sm">
                        {t('editor.no_services', 'Нет добавленных услуг')}
                    </div>
                ) : (
                    <table
                        className="w-full text-sm text-left border-collapse"
                        aria-label={t('editor.estimate_table_title', 'Таблица сметы')}
                    >
                        <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-blue-50/60 dark:bg-slate-800/80 border-b border-blue-100 dark:border-slate-800 shadow-sm">
                            <tr>
                                <th scope="col" className="px-4 py-3 font-bold">{t('editor.table.service', 'Услуга')}</th>
                                <th scope="col" className="hidden md:table-cell px-4 py-3 text-center w-20">{t('catalog.table.unit_short', 'Ед.')}</th>
                                <th scope="col" className="hidden md:table-cell px-4 py-3 text-right w-32">{t('editor.table.qty', 'К-во')}</th>
                                <th scope="col" className="hidden md:table-cell px-4 py-3 text-right w-32">{t('editor.table.price', 'Цена')}</th>
                                <th scope="col" className="hidden md:table-cell px-4 py-3 text-right w-28">{t('editor.table.total', 'Сумма')}</th>
                                <th scope="col" className="hidden md:table-cell pl-2 pr-4 py-3 w-12"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                            <AnimatePresence initial={false}>
                                {Object.entries(groupedItems).map(([catId, catItems]) => {
                                    const categoryName = getCategoryName(catId);
                                    const categoryCost = getCategorySubtotal(catId);

                                    return (
                                        <Fragment key={catId}>
                                            {/* Category Subheader Row */}
                                            <tr className="bg-slate-100/50 dark:bg-slate-800/40">
                                                <td colSpan={6} className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300">
                                                    <div className="flex justify-between items-center w-full">
                                                        <span>{categoryName}</span>
                                                        <span className="font-mono text-brand-600 dark:text-brand-400 font-bold">{t('editor.table.category_subtotal', 'Сумма: {{cost}}', { cost: roundTo(categoryCost, 0) })}</span>
                                                    </div>
                                                </td>
                                            </tr>

                                            {catItems.map(item => {
                                                const userQty = item.userQty || 0;
                                                const itemIndex = flatItemIds.indexOf(item.id);
                                                const isEven = itemIndex % 2 === 0;
                                                const adj = item.adjustment;
                                                const isIdentityAdj = !adj || (adj.op === 'mul' && adj.value === 1);
                                                // Placeholder qty uses typed adjustment (no Number() cast)
                                                const adjustedBaseQty = roundTo(applyTypedAdjustment(item.baseQty || 0, adj), 2);

                                                return (
                                                    <motion.tr
                                                        key={item.id}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{ duration: 0.15 }}
                                                        className={`transition-colors duration-150 ease-in-out group hover:bg-brand-50/20 dark:hover:bg-brand-900/10 ${
                                                            isEven ? 'bg-slate-50/40 dark:bg-slate-900/30' : 'bg-white dark:bg-slate-900'
                                                        }`}
                                                    >
                                                        <td className="px-4 py-3 align-top md:align-middle relative border-l-[3px] border-l-transparent group-hover:border-l-brand-500 transition-colors">
                                                            <div className="font-bold text-slate-700 dark:text-slate-200 leading-tight mb-1">
                                                                {item.nameKey ? t(item.nameKey, item.name) : item.name}
                                                                {/* Adjustment badge — hidden when identity (op='mul', value=1) */}
                                                                {!isIdentityAdj && (
                                                                    <span className="ml-2 text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                                                        {/* eslint-disable-next-line i18next/no-literal-string */}
                                                                        <span className="font-mono tabular-nums">
                                                                            {adj.op === 'add' ? '+' : adj.op === 'sub' ? '-' : 'x'}{adj.value}
                                                                        </span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-slate-400 dark:text-slate-500 mb-2 md:mb-0">
                                                                {categoryName}
                                                            </div>

                                                            {/* MOBILE ROW */}
                                                            <div className="md:hidden flex items-center justify-between mt-2 pb-1 h-9 gap-2">
                                                                <div className="flex items-center gap-2 h-full flex-1">
                                                                    <div className="flex items-center rounded-md px-1 h-full flex-1">
                                                                        <NumberInput
                                                                            value={userQty || 0}
                                                                            onChange={(val) => onUpdateItem(item.id, 'userQty', val)}
                                                                            placeholder={String(adjustedBaseQty)}
                                                                            isManual={userQty > 0}
                                                                            className="w-16 h-7 text-xs"
                                                                            data-testid="item-qty-input"
                                                                        />

                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (userQty > 0) onShowManualInfo();
                                                                                else onShowAutoInfo(item);
                                                                            }}
                                                                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-1 h-5 flex items-center justify-center transition-all active:scale-95 border ${
                                                                                userQty > 0
                                                                                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-800/50 hover:bg-amber-100/50 dark:hover:bg-amber-900/40 shadow-sm'
                                                                                    : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800/50 hover:bg-blue-100/50 dark:hover:bg-blue-900/40 shadow-sm'
                                                                            }`}
                                                                            aria-label={userQty > 0
                                                                                ? t('editor.manual_mode_info', 'Информация о ручном режиме ввода')
                                                                                : t('editor.auto_mode_info', 'Информация об автоматическом режиме ввода')
                                                                            }
                                                                        >
                                                                            {userQty > 0 ? 'M' : 'A'}
                                                                        </button>
                                                                    </div>
                                                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                                                    <span className="text-slate-300 dark:text-slate-700 text-xs flex items-center h-full pt-0.5 select-none">x</span>
                                                                    <div className="flex items-center rounded-md px-1 h-full flex-1">
                                                                        <NumberInput
                                                                            value={item.priceLabor || 0}
                                                                            onChange={(val) => onUpdateItem(item.id, 'priceLabor', val)}
                                                                            isManual={!!item.priceIsManual}
                                                                            className="w-16 h-7 text-xs"
                                                                            data-testid="item-price-input"
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (item.priceIsManual) onShowManualInfo();
                                                                                else onShowAutoInfo(item);
                                                                            }}
                                                                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-1 h-5 flex items-center justify-center transition-all active:scale-95 border ${
                                                                                item.priceIsManual
                                                                                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-800/50 hover:bg-amber-100/50 dark:hover:bg-amber-900/40 shadow-sm'
                                                                                    : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800/50 hover:bg-blue-100/50 dark:hover:bg-blue-900/40 shadow-sm'
                                                                            }`}
                                                                            aria-label={item.priceIsManual
                                                                                ? t('editor.manual_price_info', 'Ручная цена — не обновится при синхронизации с каталогом')
                                                                                : t('editor.catalog_price_info', 'Цена из каталога — обновится при синхронизации')
                                                                            }
                                                                        >
                                                                            {item.priceIsManual ? 'M' : 'A'}
                                                                        </button>
                                                                        <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-1 h-full flex items-center select-none">{currency}</span>
                                                                        {item.priceIsManual && onResetPriceToCatalog && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => { e.stopPropagation(); onResetPriceToCatalog(item.id); }}
                                                                                className="ml-1 p-1 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                                                                aria-label={t('editor.reset_price', 'Сбросить цену к каталогу')}
                                                                            >
                                                                                <RefreshCw size={12} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="flex items-center gap-1.5 h-full pl-1">
                                                                    <div className="font-bold font-mono tabular-nums text-slate-800 dark:text-slate-100 text-sm leading-none flex items-center h-full pt-0.5 mr-1">
                                                                        <span className="font-mono tabular-nums">{roundTo(item.cost || 0, 0)}</span>
                                                                    </div>
                                                                    {onCopyToRooms && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => onCopyToRooms(item)}
                                                                            className="w-7 h-7 flex items-center justify-center rounded-full text-slate-300 dark:text-slate-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-500 transition-all active:scale-90"
                                                                            aria-label={t('common.copy_to_rooms', 'Копировать в другие комнаты')}
                                                                        >
                                                                            <Copy size={14} aria-hidden="true" />
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => onRemoveItem(item.id)}
                                                                        className="w-7 h-7 flex items-center justify-center rounded-full text-slate-300 dark:text-slate-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 transition-all active:scale-90"
                                                                        aria-label={t('common.delete', 'Удалить')}
                                                                    >
                                                                        <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Typed adjustment control (below mobile row) */}
                                                            <div className="md:hidden">
                                                                <AdjustmentControl item={item} onUpdateItem={onUpdateItem} />
                                                            </div>
                                                        </td>

                                                        {/* DESKTOP UNIT */}
                                                        <td className="hidden md:table-cell px-4 text-center py-3">
                                                            <div className="font-medium text-slate-500 dark:text-slate-400">
                                                                {(() => {
                                                                    const unitId = item.unit || '';
                                                                    if (unitId.startsWith('m2')) return t('common.unit_m2', 'м²');
                                                                    if (unitId.startsWith('mp')) return t('common.unit_lm', 'мп');
                                                                    return t(`common.unit_${unitId}`, unitId);
                                                                })()}
                                                            </div>
                                                        </td>

                                                        {/* DESKTOP QTY + Typed adjustment control */}
                                                        <td className="hidden md:table-cell px-4 text-right py-3">
                                                            <div className="flex items-center justify-end gap-2 py-1">
                                                                <NumberInput
                                                                    value={userQty || 0}
                                                                    onChange={(val) => onUpdateItem(item.id, 'userQty', val)}
                                                                    placeholder={String(adjustedBaseQty)}
                                                                    isManual={userQty > 0}
                                                                    className="w-24 h-9"
                                                                    data-testid="item-qty-input"
                                                                />

                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (userQty > 0) onShowManualInfo();
                                                                        else onShowAutoInfo(item);
                                                                    }}
                                                                    className={`text-[9px] font-bold px-2.5 py-1 rounded-full select-none tracking-wider transition-all active:scale-95 border ${
                                                                        userQty > 0
                                                                            ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-800/50 hover:bg-amber-100/50 dark:hover:bg-amber-900/40 shadow-sm'
                                                                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800/50 hover:bg-blue-100/50 dark:hover:bg-blue-900/40 shadow-sm'
                                                                    }`}
                                                                    title={t('editor.click_for_info', 'Нажмите для справки')}
                                                                    aria-label={userQty > 0
                                                                        ? t('editor.manual_mode_info', 'Информация о ручном режиме ввода')
                                                                        : t('editor.auto_mode_info', 'Информация об автоматическом режиме ввода')
                                                                    }
                                                                >
                                                                    {userQty > 0 ? 'MAN' : 'AUTO'}
                                                                </button>
                                                            </div>
                                                            {/* Typed adjustment control — desktop */}
                                                            <div className="flex justify-end mt-1">
                                                                <AdjustmentControl item={item} onUpdateItem={onUpdateItem} />
                                                            </div>
                                                        </td>

                                                        {/* DESKTOP PRICE */}
                                                        <td className="hidden md:table-cell px-4 text-right py-3">
                                                            <div className="flex items-center justify-end gap-1">
                                                                <NumberInput
                                                                    value={item.priceLabor || 0}
                                                                    onChange={(val) => onUpdateItem(item.id, 'priceLabor', val)}
                                                                    isManual={!!item.priceIsManual}
                                                                    className="w-24 h-9"
                                                                    data-testid="item-price-input"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (item.priceIsManual) onShowManualInfo();
                                                                        else onShowAutoInfo(item);
                                                                    }}
                                                                    className={`text-[9px] font-bold px-2 py-0.5 rounded-full transition-all active:scale-95 border ${
                                                                        item.priceIsManual
                                                                            ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-800/50 hover:bg-amber-100/50 dark:hover:bg-amber-900/40 shadow-sm'
                                                                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800/50 hover:bg-blue-100/50 dark:hover:bg-blue-900/40 shadow-sm'
                                                                    }`}
                                                                    aria-label={item.priceIsManual
                                                                        ? t('editor.manual_price_info', 'Ручная цена — не обновится при синхронизации с каталогом')
                                                                        : t('editor.catalog_price_info', 'Цена из каталога — обновится при синхронизации')
                                                                    }
                                                                >
                                                                    {item.priceIsManual ? 'MAN' : 'AUTO'}
                                                                </button>
                                                                {item.priceIsManual && onResetPriceToCatalog && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); onResetPriceToCatalog(item.id); }}
                                                                        className="ml-1 p-1 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                                                        aria-label={t('editor.reset_price', 'Сбросить цену к каталогу')}
                                                                    >
                                                                        <RefreshCw size={14} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td className="hidden md:table-cell px-4 text-right font-bold font-mono tabular-nums text-slate-800 dark:text-slate-100 text-base py-3">
                                                            <span className="font-mono tabular-nums">{roundTo(item.cost || 0, 0)}</span>
                                                        </td>

                                                        <td className="hidden md:table-cell pl-2 pr-4 py-3">
                                                            <div className="flex justify-end gap-1">
                                                                {onCopyToRooms && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => onCopyToRooms(item)}
                                                                        className="w-8 h-8 flex items-center justify-center rounded-full text-slate-300 dark:text-slate-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-500 transition-all active:scale-90"
                                                                        aria-label={t('common.copy_to_rooms', 'Копировать в другие комнаты')}
                                                                    >
                                                                        <Copy size={16} aria-hidden="true" />
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => onRemoveItem(item.id)}
                                                                    className="w-8 h-8 flex items-center justify-center rounded-full text-slate-300 dark:text-slate-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 transition-all active:scale-90"
                                                                    aria-label={t('common.delete', 'Удалить')}
                                                                >
                                                                    <Trash2 size={16} aria-hidden="true" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </motion.tr>
                                                );
                                            })}
                                        </Fragment>
                                    );
                                })}
                            </AnimatePresence>
                        </tbody>
                        <tfoot className="border-t-2 border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 font-bold">
                            {/* Desktop Footer Row */}
                            <tr className="hidden md:table-row">
                                <td className="px-4 py-4 text-slate-900 dark:text-slate-100 font-bold">
                                    {t('editor.table.room_total', 'Итого по комнате')}
                                </td>
                                <td className="px-4 py-4"></td> {/* Unit */}
                                <td className="px-4 py-4"></td> {/* Qty */}
                                <td className="px-4 py-4"></td> {/* Price */}
                                <td className="px-4 py-4 text-right text-emerald-600 dark:text-emerald-400 text-lg font-mono tabular-nums">
                                    <span className="font-mono tabular-nums" data-testid="room-total-value">{roundTo(itemsTotal || 0, 0)}</span> <span className="text-xs text-slate-400 font-medium normal-case">{currency}</span>
                                </td>
                                <td className="px-4 py-4"></td>
                            </tr>
                            {/* Mobile Footer Row */}
                            <tr className="md:hidden">
                                <td colSpan={6} className="px-4 py-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-900 dark:text-slate-100 font-bold">
                                            {t('editor.table.room_total', 'Итого по комнате')}
                                        </span>
                                        <span className="text-emerald-600 dark:text-emerald-400 text-lg font-mono tabular-nums">
                                            <span className="font-mono tabular-nums" data-testid="room-total-value">{roundTo(itemsTotal || 0, 0)}</span> <span className="text-xs text-slate-400 font-medium normal-case">{currency}</span>
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
};

export const EstimateTable = memo(EstimateTableBase);
